"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import React, { useRef } from "react";

export interface Banner15Data {
  id?: string;
  status?: boolean;
  title?: string;
  image?: string;
  mobile_image?: string;
  image_alt?: string;
  title_color?: string;
  background_color?: string;
}

interface Banner15Props {
  data: Banner15Data | { data: Banner15Data };
}

function resolvePayload(data: Banner15Props["data"]): Banner15Data | undefined {
  const anyData = data as { data?: Banner15Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as Banner15Data;
  }
  return data as Banner15Data;
}

/**
 * Banner 15 — simple hero: full-bleed background image with a single overlaid
 * title aligned to the left. Title text + color and the background image are
 * admin-driven; everything reuses the shared banner columns.
 */
const Banner15 = ({ data }: Banner15Props) => {
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);
  const isMobile = useIsMobile(768);

  const {
    status = true,
    title = "",
    image = "",
    mobile_image = "",
    image_alt = "",
    title_color = "#FFFFFF",
  } = sectionData || {};

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "banner_15");
  //   }
  // }, [status]);

  if (!status) return null;

  // Use mobile-specific image when on mobile (after hydration); otherwise desktop.
  const bgImage = isMobile && mobile_image ? mobile_image : image;

  return (
    <section
      ref={ref}
      data-banner="15"
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        // White section background so the left/right margins around the
        // centered banner box are #FFF (inline style — the bg-white class
        // was not reflecting here).
        "flex w-full justify-center",
      )}
      style={{ fontFamily: "Poppins, sans-serif", backgroundColor: "#FFFFFF" }}
    >
      {/* Fixed 1114 × 534 banner box (centered). White background fills the
          area around the contained image (object-contain letterbox). */}
      <div
        className={cn(
          "relative mx-auto w-full max-w-[1114px] overflow-hidden",
          "h-[500px] lg:h-[534px]",
        )}
        style={{ backgroundColor: "#FFFFFF" }}
      >
        {bgImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={bgImage}
            alt={image_alt || ""}
            className={cn(
              "absolute inset-0 h-full w-full",
              // Fill the box on all viewports (cropped, like production).
              isMobile ? "object-cover object-center" : "object-cover",
            )}
          />
        ) : null}

        {title ? (
          <div
            className={cn(
              "absolute z-10 flex items-center",
              // Mobile: centered both axes. Desktop: left-aligned, vertical center.
              isMobile
                ? "inset-0 justify-center px-6"
                : "inset-y-0 left-0 pl-6 md:pl-[44px]",
            )}
          >
            <h1
              className={cn(
                "block whitespace-pre-line font-bold",
                "text-[24px] leading-[35.394px]",
                "lg:text-[35px] lg:capitalize lg:leading-[50px]",
                isMobile && "text-center",
              )}
              // Inline sizing — the Tailwind arbitrary w/h classes were not
              // reflecting here. Title box is 536 × 80 on desktop, fluid + centered on mobile.
              style={{
                color: title_color,
                width: isMobile ? "auto" : "536px",
                height: isMobile ? "auto" : "80px",
                maxWidth: isMobile ? "100%" : "536px",
              }}
            >
              {title}
            </h1>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default Banner15;
